import axios from "axios";

const getBackendUrl = () => {
  const env = process.env.REACT_APP_ENVIRONMENT || "development";
  if (env === "production") return process.env.REACT_APP_PROD_BACKEND_URL;
  if (env === "staging") return process.env.REACT_APP_STAGING_BACKEND_URL;
  return process.env.REACT_APP_DEV_BACKEND_URL;
};

const BASE = getBackendUrl();

const deriveNames = (email) => {
  const local = String(email || "").split("@")[0] || "user";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const first = parts[0] || "User";
  const last = parts.slice(1).join(" ") || first;
  return {
    first_name: first.charAt(0).toUpperCase() + first.slice(1),
    last_name: last.charAt(0).toUpperCase() + last.slice(1),
  };
};

const unwrap = (resp) => resp?.data?.data ?? resp?.data ?? {};

export const login = async (email, password) => {
  const resp = await axios.post(
    `${BASE}/auth/login`,
    { email, password },
    { headers: { "Content-Type": "application/json" } },
  );
  const data = unwrap(resp);

  // Phase 1: backend returns email_not_verified error for unverified accounts.
  // Surface it as a structured error so the UI can show a "resend" CTA.
  const errMeta = resp?.data?.error;
  if (errMeta?.error === "email_not_verified") {
    const e = new Error("email_not_verified");
    e.code = "email_not_verified";
    e.email = errMeta.email || email;
    throw e;
  }

  if (!data.access_token) {
    throw new Error(resp?.data?.message || "Login failed");
  }
  localStorage.setItem("accessToken", data.access_token);
  const user = {
    email: data.email,
    name: data.name,
    first_name: data.first_name,
    last_name: data.last_name,
    role: data.role,
    plan: data.plan,
  };
  localStorage.setItem("userData", JSON.stringify(user));
  return user;
};

/**
 * Returns one of:
 *   { verificationRequired: true, email }
 *   { user: { email, name, first_name, last_name, role } }
 *
 * Phase 1: free-email signups are gated behind email verification.
 * The backend returns `verification_required: true` instead of an
 * access_token; the UI shows a "check your email" screen.
 */
export const signup = async (email, password, captchaToken) => {
  const { first_name, last_name } = deriveNames(email);
  const resp = await axios.post(
    `${BASE}/auth/register`,
    {
      email,
      password,
      confirm_password: password,
      first_name,
      last_name,
      plan: "trial",
      receive_emails: false,
      recaptcha_token: captchaToken,
    },
    { headers: { "Content-Type": "application/json" } },
  );
  const data = unwrap(resp);

  if (data.verification_required) {
    return { verificationRequired: true, email: data.email || email };
  }

  if (!data.access_token) {
    throw new Error(resp?.data?.message || "Sign up failed");
  }
  localStorage.setItem("accessToken", data.access_token);
  const user = {
    email,
    name: data.name,
    first_name,
    last_name,
    role: data.role,
  };
  localStorage.setItem("userData", JSON.stringify(user));
  return { user };
};

export const resendVerification = async (email) => {
  const resp = await axios.post(
    `${BASE}/auth/resend-verification`,
    { email },
    { headers: { "Content-Type": "application/json" } },
  );
  return resp?.data?.message || "If an unverified account exists, a new verification email is on its way.";
};

export const logout = async () => {
  const token = localStorage.getItem("accessToken");
  try {
    if (token) {
      await axios.post(
        `${BASE}/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }
  } catch (e) {
    // Ignore — we still clear local state below
  }
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userData");
};

export const getCurrentUser = async () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  try {
    const resp = await axios.get(`${BASE}/account`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = unwrap(resp);
    const user = {
      email: data.email,
      name: data.name,
      first_name: data.first_name,
      last_name: data.last_name,
      role: data.role,
    };
    localStorage.setItem("userData", JSON.stringify(user));
    return user;
  } catch (e) {
    if (e?.response?.status === 401) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userData");
    }
    return null;
  }
};
